// Hash-based router. Owns which screen is mounted, extracts :params, and guards
// authenticated routes behind the fake sign-in.

import type { RouteDef, RouteParams } from './screens/types';
import { getCurrentUser } from './session';
import { renderBare, renderChrome } from './ui/shell';

const routes: RouteDef[] = [];

// Cleanup callbacks the mounted screen registered (event unsubscribes, timers).
// Run and cleared right before the next screen mounts.
let cleanups: Array<() => void> = [];

export function defineRoutes(defs: RouteDef[]): void {
  routes.push(...defs);
}

/** Register a teardown callback for the currently mounting screen. */
export function onCleanup(fn: () => void): void {
  cleanups.push(fn);
}

function runCleanups(): void {
  for (const fn of cleanups) {
    try {
      fn();
    } catch {
      /* ignore teardown errors */
    }
  }
  cleanups = [];
}

function matchRoute(path: string): { route: RouteDef; params: RouteParams } | null {
  for (const route of routes) {
    const pp = route.pattern.split('/').filter(Boolean);
    const ap = path.split('/').filter(Boolean);
    if (pp.length !== ap.length) continue;
    const params: RouteParams = {};
    let ok = true;
    for (let i = 0; i < pp.length; i++) {
      if (pp[i].startsWith(':')) params[pp[i].slice(1)] = decodeURIComponent(ap[i]);
      else if (pp[i] !== ap[i]) {
        ok = false;
        break;
      }
    }
    if (ok) return { route, params };
  }
  return null;
}

export function navigate(path: string): void {
  location.hash = path.startsWith('#') ? path : `#${path}`;
}

async function render(): Promise<void> {
  const path = location.hash.replace(/^#/, '') || '/home';
  const match = matchRoute(path);

  if (!match) {
    navigate('/home');
    return;
  }

  const { route, params } = match;
  const needsAuth = route.auth !== false;

  if (needsAuth && !getCurrentUser()) {
    if (path !== '/signin') navigate('/signin');
    return;
  }

  // Tear down the previous screen's subscriptions before mounting the next.
  runCleanups();

  const body = route.tab || route.back ? renderChrome(route) : renderBare();
  try {
    await route.screen(body, params);
  } catch (err) {
    body.html(
      `<div class="state"><div class="state__emoji">⚠️</div>${
        err instanceof Error ? err.message : String(err)
      }</div>`,
    );
  }
}

export function startRouter(): void {
  window.addEventListener('hashchange', () => {
    void render();
  });
  void render();
}

/** Re-render the current route (e.g. after a mutation). */
export function refresh(): void {
  void render();
}
